import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, XCircle, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v || 0)); }
  catch { return `$${v}`; }
};

const Row = ({ label, value, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-[#1C2A22] last:border-0">
    <div className="sm:w-[42%] text-[11px] uppercase tracking-[0.12em] text-[#6f8f7e]">{label}</div>
    <div className="sm:w-[58%] text-[13px] text-[#E9E4D0] break-words" data-testid={testid}>{value || "—"}</div>
  </div>
);

export default function VerifyInstrument() {
  const { instrumentId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/private-bank/verify-instrument/${instrumentId}`);
        if (active) setState({ loading: false, data: r.data, error: "" });
      } catch (e) {
        if (active) setState({ loading: false, data: null, error: "Could not reach the verification service." });
      }
    })();
    return () => { active = false; };
  }, [instrumentId]);

  const d = state.data;
  const found = d && d.found;

  const copyHash = async () => {
    try { await navigator.clipboard.writeText(d.esign_sha256 || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-[#07110C] text-[#E9E4D0] flex flex-col" data-testid="verify-instrument-page">
      <header className="px-6 sm:px-10 py-5 border-b border-[#143026] flex items-center gap-3">
        <ShieldCheck size={18} className="text-[#E4C97A]" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6f8f7e]">Instrument Authenticity Verification</div>
          <h1 className="text-lg font-serif">Bank of International Settlements and Securities</h1>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-5 sm:px-8 py-10">
        {state.loading ? (
          <div className="flex items-center gap-2 text-[#9fc0af]" data-testid="verify-instrument-loading">
            <Loader2 className="animate-spin" size={16} /> Verifying instrument…
          </div>
        ) : state.error ? (
          <div className="flex items-center gap-2 text-[#E89A9A]" data-testid="verify-instrument-error">
            <XCircle size={16} /> {state.error}
          </div>
        ) : !found ? (
          <div className="rounded-2xl border border-[#5A2A2A] bg-[#180F0F] p-6" data-testid="verify-instrument-notfound">
            <div className="flex items-center gap-2 text-[#E89A9A] font-semibold"><XCircle size={18} /> No matching instrument on record</div>
            <p className="text-[13px] text-[#C9B9B9] mt-2">We could not find an instrument for this verification link. If this instrument was presented to you as authentic, treat it with caution — the reference may be invalid or altered.</p>
            <p className="text-[11px] text-[#7A6A6A] mt-3 break-all">Reference: {instrumentId}</p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border border-[#1E7A4F] bg-[#0C1812] p-6" data-testid="verify-instrument-found">
              <div className="flex items-center gap-2 text-[#5FBF8F] font-semibold"><ShieldCheck size={18} /> Instrument found on record</div>
              <p className="text-[12px] text-[#9fc0af] mt-2">
                Compare the <b>E-Sign SHA-256</b> below with the hash printed on the instrument. If they match exactly, the instrument's key details have not been altered since issuance.
              </p>

              <div className="mt-5">
                <Row label="Instrument" value={d.type_name} testid="verify-type" />
                <Row label="Number" value={d.number} testid="verify-number" />
                <Row label="Amount" value={`${fmtMoney(d.amount)} ${d.currency || ""}`} testid="verify-amount" />
                <Row label="Pay to the Order of" value={d.payee} testid="verify-payee" />
                <Row label="Counterparty" value={d.counterparty} testid="verify-counterparty" />
                <Row label="Drawer (Member Institution)" value={d.drawer_institution} testid="verify-drawer" />
                <Row label="Securities Line Facility" value={d.facility_no} testid="verify-facility" />
                <Row label="E-Signed By" value={d.esignature_name} testid="verify-signer" />
                <Row label="Executed" value={(d.esigned_at || d.created_at || "").slice(0, 19).replace("T", " ")} testid="verify-executed" />
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-[#143026] bg-[#0A140F] p-5">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6f8f7e] mb-2">E-Sign SHA-256 (verification hash)</div>
              <div className="flex items-start gap-3">
                <code className="flex-1 text-[12px] text-[#E4C97A] break-all leading-relaxed" data-testid="verify-hash">{d.esign_sha256}</code>
                <button onClick={copyHash} data-testid="verify-copy-hash" className="shrink-0 inline-flex items-center gap-1 border border-[#2E6F5E]/60 hover:border-[#2E6F5E] text-[#5FBFA0] px-2.5 py-1 text-[11px] rounded-sm">
                  {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                </button>
              </div>
            </div>

            <p className="text-center text-[11px] text-[#5e7a6c] leading-relaxed mt-6">
              This verification echoes the hash stored at issuance; it is not an endorsement of value or negotiability. The
              Bank of International Settlements and Securities is a private commercial institution — not a government agency
              or central bank, and not affiliated with the U.S. Government, Treasury, IRS, Federal Reserve, or the Bank for
              International Settlements (Basel). Instruments are private commercial obligations, not FDIC/NCUA/SIPC insured.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
